import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sparkles, Send, Loader2, Bot, User, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReportChat = ({ reportId }) => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const bottomRef = useRef(null);

  const tr = language === 'tr';

  const renderContent = (text) =>
    text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));

  const suggestions = tr
    ? ['En zayıf alanım için ne yapmalıyım?', 'Sektör ortalamasına göre neredeyim?', 'İlk 6 ayda hangi adımları atmalıyım?']
    : ['What should I do about my weakest area?', 'Where do I stand vs the industry average?', 'What steps should I take in the first 6 months?'];

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/reports/${reportId}/chat/history`);
        setMessages(res.data.messages || []);
      } catch (e) {
        console.error('Chat history error:', e);
      }
    };
    load();
  }, [reportId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamText]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setStreaming(true);
    setStreamText('');

    let acc = '';
    let isError = false;
    try {
      const res = await fetch(`${API}/reports/${reportId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('gdmii-token')}`
        },
        body: JSON.stringify({ message: msg, language })
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) {
            acc += data.delta;
            setStreamText(acc);
          } else if (data.error) {
            isError = true;
            acc = acc || data.error;
          }
        }
      }
    } catch (e) {
      console.error('Chat error:', e);
      isError = true;
      acc = acc || (tr ? 'Bir hata oluştu, lütfen tekrar deneyin.' : 'An error occurred, please try again.');
    }
    setMessages((prev) => [...prev, { role: 'assistant', content: acc, isError }]);
    setStreamText('');
    setStreaming(false);
  };

  const clearChat = async () => {
    if (streaming) return;
    if (!window.confirm(tr ? 'Sohbet geçmişi silinsin ve yeni bir konuşma başlatılsın mı?' : 'Clear chat history and start a new conversation?')) return;
    try {
      await axios.delete(`${API}/reports/${reportId}/chat/history`);
      setMessages([]);
    } catch (e) {
      console.error('Clear chat error:', e);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="report-chat-panel">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <Sparkles className="mr-2 h-5 w-5 text-blue-400" />
          {tr ? 'AI Asistan' : 'AI Assistant'}
          <span className="ml-2 text-xs font-normal text-slate-500">GPT-5.4 Mini</span>
          {messages.length > 0 && (
            <button
              data-testid="chat-clear-button"
              onClick={clearChat}
              disabled={streaming}
              title={tr ? 'Sohbeti temizle' : 'Clear chat'}
              className="ml-auto flex items-center gap-1.5 text-xs font-normal text-slate-400 hover:text-red-400 transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {tr ? 'Temizle' : 'Clear'}
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-96 overflow-y-auto space-y-4 pr-2 mb-4" data-testid="chat-messages">
          {messages.length === 0 && !streaming && (
            <div className="text-center py-8">
              <Bot className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-sm mb-4">
                {tr ? 'Raporunuz hakkında soru sorun, AI danışmanınız yanıtlasın.' : 'Ask questions about your report and get AI consultant answers.'}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                data-testid={m.role === 'user' ? 'chat-message-user' : 'chat-message-assistant'}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === 'user' ? 'bg-blue-600 text-white' : m.isError ? 'bg-red-900/40 text-red-300 border border-red-800' : 'bg-slate-800 text-slate-200'
                }`}
              >
                {m.role === 'assistant' && <Bot className="h-3.5 w-3.5 inline mr-1.5 text-blue-400 -mt-0.5" />}
                {m.role === 'assistant' ? renderContent(m.content) : m.content}
              </div>
            </div>
          ))}
          {streaming && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed bg-slate-800 text-slate-200" data-testid="chat-streaming-message">
                <Bot className="h-3.5 w-3.5 inline mr-1.5 text-blue-400 -mt-0.5" />
                {streamText || <Loader2 className="h-4 w-4 inline animate-spin text-blue-400" />}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div className="flex gap-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder={tr ? 'Raporunuz hakkında bir soru yazın...' : 'Type a question about your report...'}
            disabled={streaming}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 disabled:opacity-50"
          />
          <Button
            data-testid="chat-send-button"
            onClick={() => send()}
            disabled={streaming || !input.trim()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportChat;
